import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline";

const normalizeWidth = (w) => {
  if (!w) return undefined;
  return typeof w === "number" ? `${w}px` : w;
};

const CustomTableComponent = ({
  headers = [],
  data = [],
  itemsPerPage = 8,
  settings = {},
  onRowClick = () => {},
  contentHeight = "60vh",
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const dir = sortConfig.direction === "asc" ? 1 : -1;

    return [...data].sort((a, b) => {
      const av = a?.[sortConfig.key];
      const bv = b?.[sortConfig.key];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      const aIsNum = typeof av === "number" || !isNaN(Number(av));
      const bIsNum = typeof bv === "number" || !isNaN(Number(bv));

      if (aIsNum && bIsNum) {
        const na = Number(av);
        const nb = Number(bv);
        if (na < nb) return -1 * dir;
        if (na > nb) return 1 * dir;
        return 0;
      }

      const sa = String(av).toLowerCase();
      const sb = String(bv).toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
  }, [data, sortConfig]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedData.length / itemsPerPage || 1),
  );

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [data, itemsPerPage]);

  const handleSort = (key) => {
    setCurrentPage(1);
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const handlePageChange = (page) => {
    const next = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(next);
  };

  return (
    <div className="w-full text-sm text-gray-700 border border-gray-300 rounded-lg bg-white">
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" style={{ maxHeight: contentHeight }}>
          <table className="min-w-full table-fixed border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr>
                {headers.map((header) => {
                  const isSorted = sortConfig.key === header.key;
                  const isAsc = sortConfig.direction === "asc";

                  return (
                    <th
                      key={header.key}
                      onClick={() => handleSort(header.key)}
                      style={{
                        width: normalizeWidth(header.width),
                        minWidth: normalizeWidth(header.width),
                        maxWidth: normalizeWidth(header.width),
                      }}
                      className={`px-4 py-3 border-b border-gray-300 bg-gray-100 cursor-pointer select-none ${
                        header.align === "right" ? "text-right" : "text-left"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-medium">{header.label}</span>
                        {isSorted &&
                          (isAsc ? (
                            <ArrowUpIcon className="w-4 h-4 text-blue-500" />
                          ) : (
                            <ArrowDownIcon className="w-4 h-4 text-blue-500" />
                          ))}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="bg-white">
              {paginatedData.map((row, index) => (
                <tr
                  key={index}
                  onClick={() => onRowClick(row)}
                  className="hover:bg-gray-50 cursor-pointer transition"
                >
                  {headers.map((header) => (
                    <td
                      key={header.key}
                      style={{
                        width: normalizeWidth(header.width),
                        minWidth: normalizeWidth(header.width),
                        maxWidth: normalizeWidth(header.width),
                      }}
                      className={`px-4 py-3 border-b border-gray-200 align-top ${
                        header.align === "right" ? "text-right" : "text-left"
                      } ${
                        header.nowrap
                          ? "whitespace-nowrap truncate"
                          : "whitespace-normal"
                      }`}
                    >
                      {header.key === "setting" && settings.renderSettings
                        ? settings.renderSettings(row, index)
                        : header.render
                          ? header.render(row, index)
                          : row[header.key]}
                    </td>
                  ))}
                </tr>
              ))}

              {paginatedData.length === 0 && (
                <tr>
                  <td
                    colSpan={headers.length}
                    className="px-4 py-6 text-center text-gray-400"
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-t border-gray-200 bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-3">
          <div className="text-xs text-gray-500">
            Hiển thị {paginatedData.length} / {data.length}
          </div>

          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <ChevronLeftIcon className="w-4 h-4 -ml-1" />
            </button>

            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>

            <span className="px-3 py-1 text-xs font-medium text-gray-700 border rounded-md">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>

            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <ChevronRightIcon className="w-4 h-4" />
              <ChevronRightIcon className="w-4 h-4 -ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomTableComponent;
